<template>
    <v-card elevation="5" class="tenant-back-card">
        <!-- Header -->
        <v-card-title class="d-flex align-center pb-2">
            <v-row dense align="center">
                <v-col cols="12" md="6" class="d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-domain</v-icon>
                    <span
                        :class="$vuetify.breakpoint.smAndDown
                            ? 'primary--text text-overline'
                            : 'font-weight-medium primary--text text-h6'"
                    >
                        {{ capitalize(tenant.name) }}
                    </span>
                    <v-chip
                        v-if="tenant.type"
                        x-small
                        class="ml-3 text-uppercase"
                        :color="typeColor(tenant.type)"
                        dark
                    >
                        {{ tenant.type }}
                    </v-chip>
                </v-col>

                <v-col cols="12" md="6">
                    <div :class="$vuetify.breakpoint.smAndDown ? 'd-flex justify-center' : 'd-flex justify-end'" class="gap-2">
                        <v-tooltip bottom>
                            <template #activator="{ on }">
                                <v-btn v-on="on" @click="editTenant(tenant)" fab x-small color="orange" text class="mr-2">
                                    <v-icon>mdi-pencil-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>Edit tenant</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                            <template #activator="{ on }">
                                <v-btn v-on="on" fab x-small color="red" @click="openDeleteDialog(tenant)" text class="mr-2">
                                    <v-icon>mdi-delete-outline</v-icon>
                                </v-btn>
                            </template>
                            <span>Delete tenant</span>
                        </v-tooltip>

                        <v-tooltip bottom>
                            <template #activator="{ on }">
                                <v-btn v-on="on" fab x-small color="grey" text @click="updateValue(false)">
                                    <v-icon>mdi-axis-z-rotate-clockwise</v-icon>
                                </v-btn>
                            </template>
                            <span>Flip card</span>
                        </v-tooltip>
                    </div>
                </v-col>
            </v-row>
        </v-card-title>


        <v-card-text class="pt-4">

            <!-- Basic info section -->
            <div class="section-label text-overline grey--text mb-2">General</div>
            <v-simple-table dense class="mb-4 rounded">
                <tbody>
                    <tr v-for="key in scalarKeys" :key="key">
                        <td class="field-key text-capitalize grey--text text--darken-1" style="width: 40%">
                            {{ formatKey(key) }}
                        </td>
                        <td class="field-value text-right">
                            <span v-if="isDate(key)" class="font-weight-regular">
                                {{ formatDateLarge(tenant[key]) }}
                            </span>
                            <span v-else class="font-weight-regular">
                                {{ tenant[key] }}
                            </span>
                        </td>
                    </tr>
                </tbody>
            </v-simple-table>

            <!-- Groups -->
            <template v-if="groups.length">
                <div class="section-label text-overline grey--text mb-2">Groups</div>
                <div class="d-flex flex-wrap gap-1 mb-4">
                    <v-chip
                        v-for="group in groups"
                        :key="group.id"
                        small
                        color="primary"
                        outlined
                        class="mr-1 mb-1"
                    >
                        <v-icon left x-small>mdi-account-group-outline</v-icon>
                        {{ group.name }}
                    </v-chip>
                </div>
            </template>

            <!-- Selling companies -->
            <template v-if="sellingCompanies.length">
                <div class="section-label text-overline grey--text mb-2">
                    Selling Companies
                    <v-chip x-small class="ml-1" color="grey lighten-2">{{ sellingCompanies.length }}</v-chip>
                </div>
                <div class="d-flex flex-wrap mb-4">
                    <v-chip
                        v-for="company in sellingCompanies"
                        :key="company"
                        x-small
                        class="mr-1 mb-1"
                        color="blue-grey"
                        dark
                    >
                        {{ formatCompanyName(company) }}
                    </v-chip>
                </div>
            </template>

            <!-- Timestamps -->
            <v-divider class="mb-3" />
            <div class="d-flex justify-space-between">
                <div class="text-caption grey--text">
                    <v-icon x-small class="mr-1">mdi-clock-plus-outline</v-icon>
                    Created {{ formatDateLarge(tenant.created_on) }}
                </div>
                <div class="text-caption grey--text">
                    <v-icon x-small class="mr-1">mdi-clock-edit-outline</v-icon>
                    Updated {{ formatDateLarge(tenant.updated_on) }}
                </div>
            </div>

        </v-card-text>
    </v-card>
</template>

<script>
import { clone } from '@/util/libUtil.js';
import { formatDateLarge } from '@/util/libUtil.js';

const DATE_KEYS = ['updated_on', 'created_on'];
const SKIP_KEYS = ['groups', 'selling_companies', 'updated_on', 'created_on', 'type'];

export default {
    name: 'TenantBackCard',
    props: {
        tenant: Object,
    },
    computed: {
        scalarKeys() {
            return Object.keys(this.tenant).filter(
                k => !SKIP_KEYS.includes(k) && !Array.isArray(this.tenant[k]) && typeof this.tenant[k] !== 'object'
            );
        },
        groups() {
            const g = this.tenant.groups;
            if (!g) return [];
            if (typeof g === 'string') {
                try { return JSON.parse(g); } catch { return []; }
            }
            return Array.isArray(g) ? g : [];
        },
        sellingCompanies() {
            const sc = this.tenant.selling_companies;
            if (!sc) return [];
            if (typeof sc === 'string') {
                try { return JSON.parse(sc); } catch { return []; }
            }
            return Array.isArray(sc) ? sc : [];
        },
    },
    methods: {
        formatDateLarge,
        isDate(key) {
            return DATE_KEYS.includes(key);
        },
        formatKey(key) {
            return key.replace(/_/g, ' ');
        },
        // "YOIGO_DBL_NEGOCIOS" → "Yoigo Dbl Negocios"
        formatCompanyName(name) {
            return name
                .toLowerCase()
                .split('_')
                .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ');
        },
        typeColor(type) {
            const map = { distributor: 'indigo', reseller: 'teal', partner: 'purple' };
            return map[type?.toLowerCase()] || 'grey';
        },
        openDeleteDialog(item) {
            this.$store.commit('util/deleteDialog', clone({
                toDeleteId: item.id,
                toDeleteType: 'TENANT',
                isOpenDeleteDialog: true,
            }));
        },
        updateValue(value) {
            this.$emit('input', value);
        },
        editTenant(tenant) {
            this.$store.state.tenantManagement.newTenant.form = clone({
                id: tenant.id,
                name: tenant.name,
                reward_type: tenant.reward_type,
                selling_companies: tenant.selling_companies,
                type: tenant.type,
                client_view_mode: tenant.client_view_mode,
                enforce_mfa: tenant.enforce_mfa,
            });
            this.$store.state.tenantManagement.newTenant.formType = 'Edit';
            this.$store.commit('tenantManagement/openNewTenantDialog', true);
        },
        capitalize(string) {
            return string.charAt(0).toUpperCase() + string.slice(1);
        },
    },
};
</script>

<style scoped>
.tenant-back-card {
    overflow-y: scroll;
}
.tenant-back-card .section-label {
    font-size: 0.68rem;
    letter-spacing: 0.08em;
}
.tenant-back-card .field-key {
    font-size: 0.8rem;
    padding: 6px 8px;
}
.tenant-back-card .field-value {
    font-size: 0.85rem;
    padding: 6px 8px;
}
</style>